// services/totalsService.ts

import config from "../config";

export interface AccountTotal {
  accountId: string;
  accountName: string;
  isEnabled: boolean;
  isExpense: boolean;
  isIncome: boolean;
  isFavorite: boolean;
  total: number;
}

export interface TotalsEntry {
  date: string;
  accountId: string;
  total: number;
}

export interface TotalsSum {
  date: string;
  value: number;
}

const CREDIT_SCORE_KEY = 'Credit Score: ';
const BASE_URL = config.backendUrl;

export function isCreditScore(account: AccountTotal): boolean {
  return account.accountName.trim() === CREDIT_SCORE_KEY.trim();
}

/** GET /user/:id/totals -- Get all totals of User */
export async function fetchTotals(userId: number): Promise<AccountTotal[]> {
  const response = await fetch(`${BASE_URL}/user/${userId}/totals`);
  if (!response.ok) {
    throw new Error(`Failed to fetch totals: ${response.statusText}`);
  }
  const data: Omit<AccountTotal, 'isEnabled'>[] = await response.json();
  // isEnabled not yet returned by backend — default to true
  return data.map((item) => ({ ...item, isEnabled: true }));
}

/** GET /user/:id/totals/sums -- Get all the sums calculated from all of the totals. */
export async function fetchTotalsSums(userId: number): Promise<TotalsSum[]> {
  const response = await fetch(`${BASE_URL}/user/${userId}/totals/sums`);
  if (!response.ok) {
    throw new Error(`Failed to fetch totals sums: ${response.statusText}`);
  }
  const responseJson = await response.json();
  return Object.entries(responseJson.data.sums).map(([date, sum]) => ({ 
    date: date.toString(),
    value: sum as number
  }));
}

/** POST /user/:id/totals -- Create a Totals resource for User in the current day. */
export async function postTotals(
  userId: number,
  entries: TotalsEntry[]
): Promise<void> {
  const response = await fetch(`${BASE_URL}/user/${userId}/totals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entries),
  });
  if (!response.ok) {
    throw new Error(`Failed to post totals: ${response.statusText}`);
  }
}

/** Sum all accounts excluding the credit score entry */
export function sumTotals(totals: AccountTotal[]): number {
  return totals
    .filter((a) => !isCreditScore(a))
    .reduce((acc, account) => acc + account.total, 0.00);
}

/** Extract the credit score entry, or null if not present */
export function extractCreditScore(totals: AccountTotal[]): AccountTotal | null {
  return totals.find(isCreditScore) ?? null;
}
